import React, { useMemo } from "react";

// Curated country dial-codes (biased to India + NRI hubs). Add more as needed.
export const COUNTRY_CODES = [
  { code: "+91", label: "IN +91", flag: "🇮🇳" },
  { code: "+1", label: "US +1", flag: "🇺🇸" },
  { code: "+44", label: "UK +44", flag: "🇬🇧" },
  { code: "+971", label: "AE +971", flag: "🇦🇪" },
  { code: "+974", label: "QA +974", flag: "🇶🇦" },
  { code: "+973", label: "BH +973", flag: "🇧🇭" },
  { code: "+966", label: "SA +966", flag: "🇸🇦" },
  { code: "+968", label: "OM +968", flag: "🇴🇲" },
  { code: "+965", label: "KW +965", flag: "🇰🇼" },
  { code: "+61", label: "AU +61", flag: "🇦🇺" },
  { code: "+64", label: "NZ +64", flag: "🇳🇿" },
  { code: "+65", label: "SG +65", flag: "🇸🇬" },
  { code: "+60", label: "MY +60", flag: "🇲🇾" },
  { code: "+66", label: "TH +66", flag: "🇹🇭" },
  { code: "+81", label: "JP +81", flag: "🇯🇵" },
  { code: "+82", label: "KR +82", flag: "🇰🇷" },
  { code: "+86", label: "CN +86", flag: "🇨🇳" },
  { code: "+852", label: "HK +852", flag: "🇭🇰" },
  { code: "+49", label: "DE +49", flag: "🇩🇪" },
  { code: "+33", label: "FR +33", flag: "🇫🇷" },
  { code: "+39", label: "IT +39", flag: "🇮🇹" },
  { code: "+34", label: "ES +34", flag: "🇪🇸" },
  { code: "+31", label: "NL +31", flag: "🇳🇱" },
  { code: "+41", label: "CH +41", flag: "🇨🇭" },
  { code: "+47", label: "NO +47", flag: "🇳🇴" },
  { code: "+46", label: "SE +46", flag: "🇸🇪" },
  { code: "+45", label: "DK +45", flag: "🇩🇰" },
  { code: "+353", label: "IE +353", flag: "🇮🇪" },
  { code: "+7", label: "RU +7", flag: "🇷🇺" },
  { code: "+27", label: "ZA +27", flag: "🇿🇦" },
  { code: "+55", label: "BR +55", flag: "🇧🇷" },
  { code: "+52", label: "MX +52", flag: "🇲🇽" },
  { code: "+880", label: "BD +880", flag: "🇧🇩" },
  { code: "+92", label: "PK +92", flag: "🇵🇰" },
  { code: "+94", label: "LK +94", flag: "🇱🇰" },
  { code: "+977", label: "NP +977", flag: "🇳🇵" },
];

// Split "+919999900000" into {code:"+91", number:"9999900000"}
function split(full) {
  if (!full) return { code: "+91", number: "" };
  const s = String(full).trim();
  // longest matching code wins
  const sorted = COUNTRY_CODES.slice().sort(
    (a, b) => b.code.length - a.code.length
  );
  for (const c of sorted) {
    if (s.startsWith(c.code)) {
      return { code: c.code, number: s.slice(c.code.length).replace(/^\s+/, "") };
    }
  }
  // If it already looks like a plain 10-digit number, default to +91
  return { code: "+91", number: s.replace(/^\+/, "") };
}

/**
 * Phone-with-country-code input. `value` is the full E.164-like string
 * ("+919999900000"). onChange is called with the concatenated value.
 */
export default function PhoneInput({
  value,
  onChange,
  placeholder = "10-digit number",
  testId = "phone-input",
  required = false,
}) {
  const parts = useMemo(() => split(value), [value]);

  const setCode = (c) => {
    onChange(`${c}${parts.number}`);
  };
  const setNumber = (n) => {
    // strip non-digits
    const digits = n.replace(/\D/g, "");
    onChange(`${parts.code}${digits}`);
  };

  return (
    <div className="grid grid-cols-[120px_1fr] gap-2" data-testid={testId}>
      <select
        className="select"
        value={parts.code}
        onChange={(e) => setCode(e.target.value)}
        data-testid={`${testId}-code`}
      >
        {COUNTRY_CODES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.flag} {c.code}
          </option>
        ))}
      </select>
      <input
        type="tel"
        inputMode="numeric"
        className="input"
        value={parts.number}
        onChange={(e) => setNumber(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={`${testId}-number`}
      />
    </div>
  );
}
